/**
 * Rentals hooks using React Relay
 *
 * These hooks provide access to rental data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useQueryLoader,
  usePreloadedQuery,
  PreloadedQuery,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useRentalsRelayQuery as UseRentalsRelayQuery,
  useRentalsRelayQuery$variables,
  RentalFilterInput,
  RentalSortInput,
} from './__generated__/useRentalsRelayQuery.graphql';
import type { useRentalsRelayDirectQuery as UseRentalsRelayDirectQuery } from './__generated__/useRentalsRelayDirectQuery.graphql';
import type { useRentalsRelayPaginationFragment$key } from './__generated__/useRentalsRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Pagination fragment for rentals list
 */
const RentalsListPaginationFragment = graphql`
  fragment useRentalsRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useRentalsRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "RentalFilterInput" }
    sort: { type: "[RentalSortInput!]" }
  ) {
    listRentals(first: $first, after: $after, filter: $filter, sort: $sort)
      @connection(
        key: "useRentalsRelayPaginationFragment_listRentals"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          startTime
          endTime
          duration
          endBy
          completed
          rating
          chargeTotal
          chargeAmount
          user {
            id
            phone
            fullName
            firstName
            lastName
            email
          }
          hardware {
            id
            oldId
            number
            hardwareType {
              name
            }
          }
          startLocation {
            id
            oldId
            name
          }
          endLocation {
            id
            oldId
            name
          }
          system {
            id
            oldId
            name
          }
          # organizationId is deliberately not selected. It is a calculation that
          # reaches the organization through system.organization_system, so asking
          # for it forces those two joins into the query and stops the database
          # pushing the page limit into the rental index — the list read 655k rows
          # to return 100. No consumer of this list reads it; the org-scoped view
          # already knows the organization it filtered by.
          systemId
          systemOldId
        }
      }
    }
  }
`;

/**
 * Query for paginated rentals list (uses fragment for Suspense hooks)
 */
const RentalsListQuery = graphql`
  query useRentalsRelayQuery(
    $first: Int
    $after: String
    $filter: RentalFilterInput
    $sort: [RentalSortInput!]
  ) {
    ...useRentalsRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

/**
 * Direct query for rentals list (for non-Suspense fetchQuery usage)
 * This query doesn't use fragments so fetchQuery returns the actual data
 */
const RentalsListDirectQuery = graphql`
  query useRentalsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: RentalFilterInput
    $sort: [RentalSortInput!]
  ) {
    listRentals(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          startTime
          endTime
          duration
          endBy
          completed
          rating
          chargeTotal
          chargeAmount
          user {
            id
            phone
            fullName
            firstName
            lastName
            email
          }
          hardware {
            id
            oldId
            number
            hardwareType {
              name
            }
          }
          startLocation {
            id
            oldId
            name
          }
          endLocation {
            id
            oldId
            name
          }
          system {
            id
            oldId
            name
          }
          # organizationId is deliberately not selected. It is a calculation that
          # reaches the organization through system.organization_system, so asking
          # for it forces those two joins into the query and stops the database
          # pushing the page limit into the rental index — the list read 655k rows
          # to return 100. No consumer of this list reads it; the org-scoped view
          # already knows the organization it filtered by.
          systemId
          systemOldId
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { RentalFilterInput, RentalSortInput };

/**
 * User type from GraphQL rental
 */
export interface RentalUserGraphQL {
  id: string;
  phone: string;
  fullName?: string | null;
  firstName: string;
  lastName: string;
  email?: string | null;
}

/**
 * Hardware type from GraphQL rental
 */
export interface RentalHardwareGraphQL {
  id: string;
  oldId?: string | null;
  number?: number | null;
  hardwareType?: {
    name?: string | null;
  } | null;
}

/**
 * Location type from GraphQL rental
 */
export interface RentalLocationGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

/**
 * System type from GraphQL rental
 */
export interface RentalSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

/**
 * Rental type from GraphQL - matches the fragment fields
 */
export interface RentalGraphQL {
  id: string;
  oldId?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  duration?: number | null;
  endBy?: string | null;
  completed?: boolean | null;
  rating?: number | null;
  chargeTotal?: number | null;
  chargeAmount?: number | null;
  user?: RentalUserGraphQL | null;
  hardware?: RentalHardwareGraphQL | null;
  startLocation?: RentalLocationGraphQL | null;
  endLocation?: RentalLocationGraphQL | null;
  system?: RentalSystemGraphQL | null;
  organizationId?: string | null;
  systemId?: string | null;
  systemOldId?: string | null;
}

/**
 * Simplified filter options for common use cases
 */
export interface RentalQueryFilter {
  systemId?: string;
  systemIds?: string[];
  systemOldId?: string;
  organizationId?: string;
  userId?: string;
  hardwareOldId?: string;
  couponId?: string;
  stationId?: string;
  // Status filters
  completed?: boolean;
  chargePending?: boolean;
  // Review status filter (e.g. 'pending' for rentals awaiting review)
  reviewStatus?: string;
  // Time filters
  startTimeAfter?: string;
  startTimeBefore?: string;
  // Search pattern (for phone/name search)
  pattern?: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching rentals list using Relay
 *
 * This hook uses Relay's useLazyLoadQuery which suspends while loading.
 * Wrap with Suspense boundary.
 *
 * @example
 * function RentalList() {
 *   const { data, loadNext, hasNext, isLoadingNext } = useRentalsRelay({
 *     systemId: 'uuid',
 *   });
 *
 *   return (
 *     <>
 *       {data.map(rental => <div key={rental.id}>{rental.user?.fullName}</div>)}
 *       {hasNext && <button onClick={() => loadNext(100)}>Load More</button>}
 *     </>
 *   );
 * }
 *
 * // With Suspense
 * <Suspense fallback={<Loading />}>
 *   <RentalList />
 * </Suspense>
 */
export function useRentalsRelay(
  filter?: RentalQueryFilter,
  options?: {
    limit?: number;
    sort?: RentalSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'START_TIME', order: 'DESC' }];

  const builtFilter = buildRelayFilter(filter);
  const variables: useRentalsRelayQuery$variables = {
    first: limit,
    sort,
    // Only include filter if it's defined (not null/undefined)
    ...(builtFilter ? { filter: builtFilter } : {}),
  };

  // Initial query - suspends while loading
  const queryData = useLazyLoadQuery<UseRentalsRelayQuery>(RentalsListQuery, variables);

  // Pagination fragment for load more
  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseRentalsRelayQuery,
    useRentalsRelayPaginationFragment$key
  >(RentalsListPaginationFragment, queryData);

  // Extract nodes from edges
  const nodes = data.listRentals?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as RentalGraphQL[],
    count: data.listRentals?.count,
    pageInfo: data.listRentals?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

// ============================================================================
// Non-Suspense Hooks (with loading state)
// ============================================================================

/**
 * Hook that returns a query loader for rentals list
 *
 * This provides explicit loading state without requiring Suspense.
 * Call `loadQuery` to start fetching, then pass `queryRef` to a child component.
 */
export function useRentalsRelayLoader(initialFilter?: RentalQueryFilter) {
  const initialBuiltFilter = buildRelayFilter(initialFilter);
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<UseRentalsRelayQuery>(
    RentalsListQuery,
    initialFilter
      ? {
          first: 100,
          sort: [{ field: 'START_TIME', order: 'DESC' }],
          ...(initialBuiltFilter ? { filter: initialBuiltFilter } : {}),
        }
      : undefined
  );

  const load = (
    filter?: RentalQueryFilter,
    options?: { limit?: number; sort?: RentalSortInput[] }
  ) => {
    const limit = options?.limit ?? 100;
    const sort = options?.sort ?? [{ field: 'START_TIME', order: 'DESC' }];
    const builtFilter = buildRelayFilter(filter);

    loadQuery({
      first: limit,
      sort,
      ...(builtFilter ? { filter: builtFilter } : {}),
    });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded rentals query
 *
 * Use with useRentalsRelayLoader. Still suspends, but the data
 * is already loading so suspension is minimal.
 */
export function useRentalsRelayPreloaded(queryRef: PreloadedQuery<UseRentalsRelayQuery>) {
  const queryData = usePreloadedQuery<UseRentalsRelayQuery>(RentalsListQuery, queryRef);

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseRentalsRelayQuery,
    useRentalsRelayPaginationFragment$key
  >(RentalsListPaginationFragment, queryData);

  const nodes = data.listRentals?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as RentalGraphQL[],
    count: data.listRentals?.count,
    pageInfo: data.listRentals?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for rentals data
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch, isFetched, count, hasMore } = useRentalsRelayQuery({
 *   systemId: 'uuid',
 * });
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.map(r => r.user?.fullName)}</div>;
 */
export function useRentalsRelayQuery(
  filter?: RentalQueryFilter,
  options?: {
    limit?: number;
    sort?: RentalSortInput[];
    enabled?: boolean;
    after?: string | null;
  }
) {
  const [data, setData] = useState<RentalGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'START_TIME' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;
  // Convert null to undefined to avoid sending null to GraphQL
  const after = options?.after || undefined;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const builtFilter = buildRelayFilter(filter);
        // Build variables object, explicitly excluding null/undefined values
        // to prevent GraphQL from receiving null parameters
        const variables: Record<string, unknown> = {
          first: limit,
          sort,
        };
        if (after) {
          variables.after = after;
        }
        if (builtFilter) {
          variables.filter = builtFilter;
        }

        const result = await fetchQuery<UseRentalsRelayDirectQuery>(
          relayEnvironment,
          RentalsListDirectQuery,
          variables as useRentalsRelayQuery$variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listRentals?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
          setData(nodes as RentalGraphQL[]);
          setCount(result.listRentals?.count ?? undefined);
          setHasMore(result.listRentals?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listRentals?.pageInfo?.endCursor ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch rentals'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, after, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds a Relay/GraphQL RentalFilterInput from the simplified filter interface
 */
function buildRelayFilter(filter?: RentalQueryFilter): RentalFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: RentalFilterInput = {};

  if (filter.systemIds && filter.systemIds.length > 0) {
    relayFilter.systemId = { in: filter.systemIds };
  } else if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.userId) {
    relayFilter.userId = { eq: filter.userId };
  }
  if (filter.hardwareOldId) {
    relayFilter.hardwareOldId = { eq: filter.hardwareOldId };
  }
  if (filter.couponId) {
    relayFilter.couponId = { eq: filter.couponId };
  }
  if (filter.stationId) {
    // Filter by start or end location
    relayFilter.or = [
      { startLocationOldId: { eq: filter.stationId } },
      { endLocationOldId: { eq: filter.stationId } },
    ];
  }
  if (filter.completed !== undefined) {
    relayFilter.completed = { eq: filter.completed };
  }
  if (filter.chargePending !== undefined) {
    relayFilter.chargePending = { eq: filter.chargePending };
  }
  if (filter.reviewStatus) {
    relayFilter.reviewStatus = { eq: filter.reviewStatus };
  }
  if (filter.startTimeAfter) {
    relayFilter.startTime = {
      ...relayFilter.startTime,
      greaterThanOrEqual: filter.startTimeAfter,
    };
  }
  if (filter.startTimeBefore) {
    relayFilter.startTime = {
      ...relayFilter.startTime,
      lessThanOrEqual: filter.startTimeBefore,
    };
  }
  // Pattern search - search by user phone or name. The API rejects `or`
  // nested inside the user relationship filter, so each OR branch repeats
  // the user relationship at the top level.
  if (filter.pattern) {
    const pattern = `%${filter.pattern}%`;
    const patternOr: RentalFilterInput[] = [
      { user: { fullName: { ilike: pattern } } },
      { user: { firstName: { ilike: pattern } } },
      { user: { lastName: { ilike: pattern } } },
    ];
    // user.phone is a :string_integer whose filter type has no pattern
    // operators, so phone only matches numeric terms, and only exactly
    if (/^\d+$/.test(filter.pattern)) {
      patternOr.push({ user: { phone: { eq: filter.pattern } } });
    }
    if (relayFilter.or) {
      // the station filter already claimed the top-level or; AND the groups
      relayFilter.and = [{ or: relayFilter.or }, { or: patternOr }];
      delete relayFilter.or;
    } else {
      relayFilter.or = patternOr;
    }
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

export { buildRelayFilter };
